import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, Plus, ArrowLeft } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@travellegacy/nexops-design-system';
import { Button } from '@travellegacy/nexops-design-system';
import { Textarea } from '@travellegacy/nexops-design-system';
import { Select } from '@travellegacy/nexops-design-system';
import { FeedbackCard } from '../components/FeedbackCard';
import { usePublicFeed, useCreateMicroFeedback } from '../hooks/useMicroFeedbacks';
import { useEmployees } from '@/features/people-core/hooks/useEmployees';
import { useAuth } from '@/shared/contexts/AuthContext';
import { Permission, hasPermission } from '@/shared/types/permissions';
import type { CreateMicroFeedbackInput } from '../types';

/**
 * Página de Micro-feedbacks (Feed/Mural)
 */
export function MicroFeedbacksPage() {
  const { user } = useAuth();
  const navigate = useNavigate();

  // Verificar se tem permissão de gestão
  useEffect(() => {
    if (user && !hasPermission(user.role, user.permissions, Permission.FEEDBACK_PDI_MANAGEMENT)) {
      navigate('/feedback-pdi/check-ins', { replace: true });
    }
  }, [user, navigate]);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState<Partial<CreateMicroFeedbackInput>>({
    content: '',
    type: 'RECOGNITION',
    visibility: 'PUBLIC',
    toEmployeeId: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const { data, isLoading } = usePublicFeed();
  const createFeedback = useCreateMicroFeedback();
  const { data: employees } = useEmployees();

  const handleChange = (field: string, value: unknown) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const handleSubmit = async () => {
    // Validações
    const newErrors: Record<string, string> = {};

    if (!formData.toEmployeeId) {
      newErrors.toEmployeeId = 'Selecione um colaborador';
    }

    if (!formData.content || formData.content.trim().length === 0) {
      newErrors.content = 'Digite o feedback';
    } else if (formData.content.length > 280) {
      newErrors.content = 'Máximo de 280 caracteres';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    await createFeedback.mutateAsync(formData as CreateMicroFeedbackInput);

    // Resetar form e voltar para lista
    setFormData({
      content: '',
      type: 'RECOGNITION',
      visibility: 'PUBLIC',
      toEmployeeId: '',
    });
    setShowForm(false);
  };

  const handleCancel = () => {
    setFormData({
      content: '',
      type: 'RECOGNITION',
      visibility: 'PUBLIC',
      toEmployeeId: '',
    });
    setErrors({});
    setShowForm(false);
  };

  // Se está mostrando o formulário, renderiza apenas ele
  if (showForm) {
    return (
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button variant="outline" size="sm" onClick={handleCancel}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Voltar
          </Button>
          <div>
            <h2 className="text-2xl font-bold">Enviar Micro-feedback</h2>
            <p className="text-muted-foreground">Feedback rápido e pontual (máx. 280 caracteres)</p>
          </div>
        </div>

        {/* Formulário */}
        <Card>
          <CardHeader>
            <CardTitle>Informações do Feedback</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <Select
              label="Para (Colaborador)"
              value={formData.toEmployeeId}
              onChange={(e) => handleChange('toEmployeeId', e.target.value)}
              error={errors.toEmployeeId}
              options={[
                { value: '', label: 'Selecione um colaborador' },
                ...(employees?.map((emp) => ({
                  value: emp.id,
                  label: emp.user?.name || 'Sem nome',
                })) || []),
              ]}
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Select
                label="Tipo de Feedback"
                value={formData.type}
                onChange={(e) => handleChange('type', e.target.value)}
                options={[
                  { value: 'RECOGNITION', label: 'Reconhecimento' },
                  { value: 'DEVELOPMENT', label: 'Desenvolvimento' },
                  { value: 'CORRECTION', label: 'Correção' },
                  { value: 'ALIGNMENT', label: 'Alinhamento' },
                ]}
              />

              <Select
                label="Visibilidade"
                value={formData.visibility}
                onChange={(e) => handleChange('visibility', e.target.value)}
                options={[
                  { value: 'PUBLIC', label: 'Público (todos veem)' },
                  { value: 'PRIVATE', label: 'Privado (só envolvidos e gestor)' },
                ]}
              />
            </div>

            <div>
              <Textarea
                label="Feedback"
                value={formData.content}
                onChange={(e) => handleChange('content', e.target.value)}
                error={errors.content}
                placeholder="Digite seu feedback (máximo 280 caracteres)..."
                rows={4}
              />
              <p className="text-xs text-muted-foreground mt-1">
                {formData.content?.length || 0}/280 caracteres
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Ações */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={handleCancel}>
                Cancelar
              </Button>
              <Button onClick={handleSubmit} disabled={createFeedback.isPending}>
                {createFeedback.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Enviando...
                  </>
                ) : (
                  'Enviar Feedback'
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Micro-feedbacks</h1>
          <p className="text-muted-foreground">Feed público de reconhecimentos e feedbacks</p>
        </div>

        <Button onClick={() => setShowForm(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Enviar Feedback
        </Button>
      </div>

      {/* Feed */}
      <div className="space-y-4">
        {data && data.feedbacks.length > 0 ? (
          data.feedbacks.map((feedback) => <FeedbackCard key={feedback.id} feedback={feedback} />)
        ) : (
          <Card>
            <CardContent className="pt-6 text-center text-muted-foreground">
              Nenhum feedback público ainda.
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
